import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';

interface AISummaryData {
  short_summary: string | null;
  long_summary: string | null;
  pro_position_summary: string | null;
  con_position_summary: string | null;
  created_at?: string;
  enabled_features?: {
    ai_summary_short: boolean;
    ai_summary_long: boolean;
    ai_summary_pro: boolean;
    ai_summary_con: boolean;
  };
}

interface AISummarySectionProps {
  aiSummary: AISummaryData | null;
  className?: string;
}

const AISummarySection: React.FC<AISummarySectionProps> = ({ aiSummary, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Don't render if no AI summary data or no enabled features
  if (!aiSummary) {
    return null;
  }

  const enabledFeatures = aiSummary.enabled_features || {
    ai_summary_short: true,
    ai_summary_long: true,
    ai_summary_pro: true,
    ai_summary_con: true
  };

  // Check if we have any content to display
  const hasShortSummary = enabledFeatures.ai_summary_short && aiSummary.short_summary;
  const hasLongSummary = enabledFeatures.ai_summary_long && aiSummary.long_summary;
  const hasProSummary = enabledFeatures.ai_summary_pro && aiSummary.pro_position_summary;
  const hasConSummary = enabledFeatures.ai_summary_con && aiSummary.con_position_summary;

  // Don't render if nothing to show
  if (!hasShortSummary && !hasLongSummary && !hasProSummary && !hasConSummary) {
    return null;
  }

  const hasExpandableContent = hasLongSummary || hasProSummary || hasConSummary;

  return (
    <div className={`card ${className}`}>
      {/* Header - matches Bill Summary card styling */}
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-500" />
          <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
            AI Summary
          </h3>
          <span className="text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
            Beta
          </span>
        </div>
        {hasExpandableContent && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
            ) : (
              <ChevronDown className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
            )}
          </button>
        )}
      </div>

      {/* Short Summary - Always visible */}
      {hasShortSummary && (
        <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
          {aiSummary.short_summary}
        </p>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark space-y-4">
          {/* Long Summary */}
          {hasLongSummary && (
            <div>
              <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Detailed Analysis
              </h4>
              <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                {aiSummary.long_summary}
              </p>
            </div>
          )}

          {/* Pro/Con Side by Side */}
          {(hasProSummary || hasConSummary) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Supporting Arguments */}
              {hasProSummary && (
                <div className="p-4 bg-success-50 dark:bg-success-900/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-full bg-success-100 dark:bg-success-900/30">
                      <ThumbsUp className="w-3.5 h-3.5 text-success-600 dark:text-success-400" />
                    </div>
                    <h5 className="text-sm font-semibold text-success-700 dark:text-success-400">
                      Supporting Arguments
                    </h5>
                  </div>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                    {aiSummary.pro_position_summary}
                  </p>
                </div>
              )}

              {/* Opposing Arguments */}
              {hasConSummary && (
                <div className="p-4 bg-error-50 dark:bg-error-900/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-full bg-error-100 dark:bg-error-900/30">
                      <ThumbsDown className="w-3.5 h-3.5 text-error-600 dark:text-error-400" />
                    </div>
                    <h5 className="text-sm font-semibold text-error-700 dark:text-error-400">
                      Opposing Arguments
                    </h5>
                  </div>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                    {aiSummary.con_position_summary}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Footer with disclaimer */}
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary pt-2">
            AI-generated summary. May not reflect all perspectives. Review official bill text for complete information.
          </p>
        </div>
      )}
    </div>
  );
};

export default AISummarySection;
